

import { Vue, Component, Watch } from 'vue-property-decorator';
import TabelaMesa from './components/TabelaMesa.vue';
import FiltroMesa from './components/FiltroMesa.vue';
import { Filter, VinculoMesa } from './types-mesa';
import MesaOperacoesController from '@/assets/ts/controller/MesaOperacoesController';
import DateUtil from '@tekvue/assets/utils/DateUtil';
import { hoursInIntervals } from './components/utils-mesa';
import SnackbarUtils from '@tekvue/assets/utils/SnackbarUtils';

@Component({
  name: 'MesaOperacoes',
  components: {
    TabelaMesa,
    FiltroMesa,
  },
})
export default class MesaOperacoes extends Vue {
  private readonly REFRESH_TIMEOUT = 1000 * 60 * 5; // 5 minutes

  filters: Filter = null;
  descriptionFilters: string = '';
  refreshInterval: NodeJS.Timeout = null;
  loadedOnce = false;

  currentHour = (new Date()).getHours();

  vinculos: VinculoMesa[] = [];
  showDetails: boolean = false;

  isLoading: boolean = false;

  get title(): string {
    const defaultTitle = 's.'
    return this.descriptionFilters || defaultTitle;
  }

  get unidade(): string {
    return this.$store.getters.estruturaGerencialAtual ? this.$store.getters.estruturaGerencialAtual.NRESTRUTURAM : '';
  }

  get hours(): string[] {
    if (!this.filters) return [];

    if (this.filters.horas === 'DIA_INTEIRO') {
      return Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    }

    if (this.filters.horas === 'PERSONALIZADO') {
      return this.filters.horasSelecionadas;
    }

    if (this.filters.horas === 'INTERVALO') {
      const startHour = parseInt(this.filters.horaInicial);
      const endHour = parseInt(this.filters.horaFinal);
      return Array.from({ length: endHour - startHour + 1 }, (_, i) => (startHour + i).toString().padStart(2, '0'));
    }

    if (this.filters.horas === 'AUTO') {
      const currentHour = this.currentHour;
      const quantidade = this.filters.quantidadeHoras;
      const startHour = currentHour - quantidade; // Pode ser negativo, indicando uma hora do dia anterior

      /** -1 -> 23; -2 -> 22, etc */
      function normalizeHour(hour: number): number {
        return hour < 0 ? hour + 24 : hour;
      }

      return Array.from({ length: quantidade + 1 }, (_, i) => (normalizeHour(startHour + i)).toString().padStart(2, '0'));
    }
  }

  get filteredRows() {
    if (!this.filters || this.filters.workStatus === 'TODOS') {
      return this.vinculos;
    }

    const isWorking = this.filters.workStatus === 'TRABALHANDO';
    return this.vinculos.filter(vinculo => {
      const workedHours = new Set(hoursInIntervals(vinculo.horas_trabalhadas));
      return isWorking ? workedHours.size > 0 : workedHours.size === 0;
    });
  }

  get showNoVinculoMessage(): boolean {
    return this.vinculos.length === 0 && this.loadedOnce; // Previne que a mensagem apareça ao carregar pela 1a vez.
  }

  @Watch('unidade')
  private unidadeOnChange() {
    this.loadMesaOperacoes();
  }

  private created() {
    this.startCurrentHourUpdater();
  }

  updateCurrentHour(): void {
    this.currentHour = (new Date()).getHours();
  }

  startCurrentHourUpdater(): void {
    setTimeout(() => {
      this.updateCurrentHour(); // Will update in the next hour

      setInterval(() => {
        this.updateCurrentHour(); // Updates every hour
      }, 1000 * 60 * 60);
    }, DateUtil.getMsUntilNextHour());
  }

  startRefreshInterval(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = setInterval(() => {
      this.loadMesaOperacoes(false);
    }, this.REFRESH_TIMEOUT);
  }

  toggleShowDetails() {
    this.showDetails = !this.showDetails;
  }

  onFiltersApplied(filters: Filter) {
    const changedDate = !this.filters || this.filters.data !== filters.data;
    this.filters = filters;

    if (changedDate) {
      this.loadMesaOperacoes();
    }
  }

  private verifyUnidade(): boolean {
    if (this.unidade === '-1') {
      SnackbarUtils.showMessage('Não é possível utilizar a estrutura "-- Todas --".', SnackbarUtils.TYPE_ALERT);
      return false;
    }

    return true;
  }

  async loadMesaOperacoes(showLoading: boolean = true) {
    if (!this.filters || !this.unidade || this.isLoading || !this.verifyUnidade()) return;

    this.isLoading = true;
    this.startRefreshInterval();

    try {
      this.vinculos = await MesaOperacoesController.getMesaOperacoes(this.filters.data, this.unidade, showLoading);
      this.loadedOnce = true;
    } catch (error) {
      SnackbarUtils.showMessage('Não foi possível carregar os dados da Mesa de Operações.', SnackbarUtils.TYPE_ERROR, 60000);
    } finally {
      this.isLoading = false;
    }
  }
}

